import { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { KeyRound, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [60, 180, 300, 600];

/**
 * PIN entry dialog for «Έγκριση από Manager» (temporary override).
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (boolean) => void
 *   reason?: string  — optional context shown to the manager
 *   onGranted?: () => void  — called on success (so caller can retry action)
 */
export default function ManagerOverrideDialog({ open, onOpenChange, reason, onGranted }) {
  const { requestOverride } = useAuth();
  const [pin, setPin] = useState("");
  const [duration, setDuration] = useState(300);
  const [busy, setBusy] = useState(false);

  const reset = () => { setPin(""); setDuration(300); setBusy(false); };

  const submit = async () => {
    if (!pin || pin.length < 4) {
      toast.error("PIN τουλάχιστον 4 ψηφία");
      return;
    }
    setBusy(true);
    try {
      const d = await requestOverride(pin, { duration_seconds: duration, reason });
      toast.success(`Έγκριση από «${d.manager.name}» · ${Math.round(duration / 60)} λεπτά`);
      onOpenChange(false);
      reset();
      onGranted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="manager-override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-amber-600" />
            Έγκριση από Manager
          </DialogTitle>
          <DialogDescription>
            {reason
              ? `Απαιτείται έγκριση για: «${reason}». Ο manager βάζει το PIN του και η συνεδρία αναβαθμίζεται προσωρινά.`
              : "Ο manager βάζει το PIN του εδώ και η συνεδρία του ταμία αποκτά προσωρινά τα δικαιώματά του."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold flex items-center gap-1.5">
              <KeyRound size={12} /> PIN Manager
            </Label>
            <Input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              maxLength={12}
              autoFocus
              placeholder="••••"
              className="text-lg font-mono"
              data-testid="manager-override-pin"
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            />
          </div>
          <div>
            <Label className="text-xs font-bold mb-1.5 block">Διάρκεια</Label>
            <div className="grid grid-cols-4 gap-2">
              {PRESETS.map((s) => (
                <Button
                  key={s}
                  variant={duration === s ? "default" : "outline"}
                  size="sm"
                  className={duration === s ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}
                  onClick={() => setDuration(s)}
                  data-testid={`manager-override-duration-${s}`}
                >
                  {s >= 60 ? `${s / 60} λεπτά` : `${s}s`}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="manager-override-cancel">
            <X size={14} className="mr-1" />
            Άκυρο
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !pin}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="manager-override-submit"
          >
            <ShieldCheck size={14} className="mr-1" />
            Έγκριση
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Compact badge shown in the Layout while a manager override is active.
 * Renders nothing when there is no active override.
 */
export function ManagerOverrideBadge() {
  const { override, clearOverride } = useAuth();
  if (!override) return null;
  const mm = Math.floor(override.remaining_sec / 60);
  const ss = String(override.remaining_sec % 60).padStart(2, "0");
  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-100 border-2 border-amber-400 text-amber-900 shadow-sm"
      data-testid="manager-override-badge"
    >
      <ShieldCheck size={14} />
      <span className="text-xs font-bold whitespace-nowrap">
        Override: <strong>{override.manager?.name}</strong>
      </span>
      <span className="font-mono text-sm tabular tracking-wide font-bold" data-testid="manager-override-countdown">
        {mm}:{ss}
      </span>
      <button
        onClick={clearOverride}
        title="Λήξη override"
        className="ml-1 text-amber-700 hover:text-amber-900"
        data-testid="manager-override-clear"
      >
        <X size={14} />
      </button>
    </div>
  );
}
